import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Copy, Loader2, Mail, Plus, Trash2 } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("grc_admin_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const inputCls =
  "mt-1 w-full rounded-xl border border-linen-border focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 p-2.5 text-sm text-navy bg-white";
const labelCls = "block text-[11px] font-bold uppercase tracking-[0.16em] text-ink-500";

const CopyLink = ({ url, label, testid }) => (
  <div className="flex items-center gap-2 min-w-0">
    <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500 w-14 shrink-0">{label}</span>
    <code className="text-[11px] text-ink-600 truncate">{url}</code>
    <button
      onClick={async () => {
        const ok = await copyText(url);
        toast[ok ? "success" : "error"](ok ? `${label} link copied` : "Copy it manually");
      }}
      data-testid={testid}
      className="shrink-0 text-teal hover:text-navy transition-colors"
    >
      <Copy size={13} />
    </button>
  </div>
);

export const BrokeragesCard = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", leader_name: "", leader_email: "" });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState("");

  const load = () => api.get("/admin/brokerages").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.leader_email.trim()) {
      toast.error("Brokerage name and the leader's email are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/brokerages", form);
      toast.success("Brokerage added — send them the roster link when you're ready.");
      setForm({ name: "", leader_name: "", leader_email: "" });
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not add that brokerage.");
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async (row) => {
    setSending(row.id);
    try {
      const { data } = await api.post(`/admin/brokerages/${row.id}/magic-link`);
      toast.success(`Roster link sent to ${data.sent_to}`);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not send that link.");
    } finally {
      setSending("");
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/admin/brokerages/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Brokerage removed");
    } catch {
      toast.error("Could not remove that brokerage.");
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-6 sm:p-7" data-testid="brokerages-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-navy text-lime shrink-0">
          <Building2 size={18} />
        </span>
        <div>
          <h3 className="font-heading font-medium text-xl text-navy">Brokerage rosters</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Each brokerage gets a branded invite link (agents who apply through it are tagged automatically)
            and a password-free roster view for the owner.
          </p>
        </div>
      </div>

      <form onSubmit={create} className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className={labelCls}>Brokerage name</span>
          <input data-testid="brokerage-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bluewater Realty Group" className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Owner / leader</span>
          <input data-testid="brokerage-leader" value={form.leader_name} onChange={(e) => setForm({ ...form, leader_name: e.target.value })} placeholder="Sarah Chen" className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Leader email</span>
          <input data-testid="brokerage-email" type="email" value={form.leader_email} onChange={(e) => setForm({ ...form, leader_email: e.target.value })} placeholder="sarah@bluewater.com" className={inputCls} />
        </label>
        <button
          type="submit"
          disabled={busy}
          data-testid="brokerage-create"
          className="sm:col-span-3 justify-self-start inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold text-sm px-5 py-2.5 transition-colors"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add brokerage
        </button>
      </form>

      <div className="mt-6 space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="rounded-2xl bg-linen/70 border border-linen-border p-4" data-testid={`brokerage-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="font-semibold text-navy">{r.name}</div>
              <span className="text-[11px] text-ink-500">{r.leader_name} · {r.leader_email}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-teal-light text-teal px-2 py-0.5">
                {r.approved} approved / {r.agents} applied
              </span>
              <div className="ml-auto flex items-center gap-3">
                <button
                  onClick={() => sendLink(r)}
                  disabled={sending === r.id}
                  data-testid={`brokerage-send-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal hover:text-navy transition-colors"
                >
                  {sending === r.id ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                  {r.link_sent_at ? "Resend roster link" : "Send roster link"}
                </button>
                <button onClick={() => remove(r)} data-testid={`brokerage-delete-${r.id}`} className="text-ink-400 hover:text-red-600 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              <CopyLink url={r.invite_url} label="Invite" testid={`brokerage-copy-invite-${r.id}`} />
              <CopyLink url={r.roster_url} label="Roster" testid={`brokerage-copy-roster-${r.id}`} />
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-ink-500" data-testid="brokerages-empty">
            No brokerages yet. Add one and we'll generate both links.
          </p>
        )}
      </div>
    </div>
  );
};

export default BrokeragesCard;
